import { ReactNode, MouseEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuoteModal } from '@/contexts/QuoteModalContext';
import { Button, buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { VariantProps } from 'class-variance-authority';
import { trackModalOpen, trackCTAClick } from '@/lib/analytics';

interface QuoteTriggerButtonProps extends VariantProps<typeof buttonVariants> {
  children: ReactNode;
  className?: string;
  category?: string;
  asChild?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}

/**
 * A button that opens the Quote Modal when clicked.
 * Use this to replace any CTA that should trigger the quote form.
 */
export const QuoteTriggerButton = ({ 
  children, 
  className,
  category,
  variant,
  size,
  disabled,
  onClick,
  ...props 
}: QuoteTriggerButtonProps) => {
  const { openQuoteModal } = useQuoteModal();
  const location = useLocation();

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Call any additional onClick handler
    onClick?.();
    
    // Extract button label text for attribution
    const buttonLabel = typeof children === 'string' 
      ? children 
      : (e.currentTarget.textContent || 'Get Quote');
    
    // Track the CTA click and modal open
    trackCTAClick(buttonLabel, location.pathname, '/quote');
    trackModalOpen('quote');
    
    openQuoteModal({ category, buttonLabel });
  };

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      className={cn(className)}
      onClick={handleClick}
      disabled={disabled}
      data-quote-trigger="true"
      {...props}
    >
      {children}
    </Button>
  );
};

export default QuoteTriggerButton;
